'use client';

import { TravelReviewCGType } from '@/types/graphql/root';
import Link from 'next/link';
import { getDisplayCreatedAt } from '@/utils/date-calc';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getListTravelReview } from '@/utils/actions';
import dayjs from 'dayjs';
import SharingTripsListTopBar from './sharing-trips-list-top-bar';
import Magician6051 from '@svg/sharing-trips/magician_60_51.svg';
import NewIcon67 from '@svg/sharing-trips/new_icon_6_7.svg';
import CommentBalloon1616 from '@svg/sharing-trips/comment_balloon_16_16.svg';
import useActionHandler from '@/app/hooks/useActionHandler';

export default function ClientBoardSharingTrips({
  initPosts,
  limit,
  hasNext,
}: {
  initPosts: TravelReviewCGType[];
  limit: number;
  hasNext: boolean;
}) {
  const observerTargetRef = useRef<HTMLDivElement>(null);

  const [offset, setOffset] = useState(initPosts.length);
  const [hasMore, setHasMore] = useState(hasNext);
  const [posts, setPosts] = useState(initPosts);

  const {
    execute: loadListTravelReview,
    data: listTravelReviewResultData,
    isLoading,
  } = useActionHandler({
    action: getListTravelReview,
    key: 'travelReviews',
  });

  useEffect(() => {
    if (listTravelReviewResultData && listTravelReviewResultData.listTravelReview) {
      const currentData = listTravelReviewResultData.listTravelReview;
      const edges = currentData.edges ?? [];
      const hasNextValue = currentData.hasNext ?? false;

      setPosts((prevPosts) => {
        const existingIds = new Set(prevPosts.map((p) => p.id));
        const newEdges = edges.filter((edge) => edge && edge.id && !existingIds.has(edge.id));
        return [...prevPosts, ...newEdges];
      });
      setOffset((prevOffset) => prevOffset + edges.length);
      setHasMore(hasNextValue);
    }
  }, [listTravelReviewResultData]);

  const onClickMore = useCallback(async () => {
    await loadListTravelReview(offset, limit);
  }, [offset, limit]);

  const getCommentCount = useCallback((post: TravelReviewCGType) => {
    const parentCount = post.comments?.length ?? 0;
    const children = post.comments?.map((comment) => comment?.children).flat(Infinity);

    return parentCount + (children?.length ?? 0);
  }, []);

  const getPostType = useCallback((post: TravelReviewCGType) => {
    if (post.category === 'AFTER_TRAVEL') return '여행후기';
    else return '여행예정';
  }, []);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !isLoading && hasMore) {
        onClickMore();
      }
    },
    [isLoading, hasMore],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null, // 뷰포트 기준
      rootMargin: '50px', // 하단에서 50px 떨어진 지점에서 트리거
      threshold: 0.1, // 10% 이상 보이면 트리거
    });

    if (observerTargetRef.current) {
      observer.observe(observerTargetRef.current);
    }

    return () => {
      if (observerTargetRef.current) {
        observer.unobserve(observerTargetRef.current);
      }
    };
  }, [handleObserver]);

  return (
    <div className='w-full pb-60pxr loaded-wrap'>
      <SharingTripsListTopBar />
      <div className='w-full pt-56pxr'>
        <div className='px-20pxr py-16pxr'>
          <div className='w-full px-20pxr py-8pxr flex items-center gap-12pxr bg-new-point-50 rounded-8pxr'>
            <Magician6051 />
            <p className='text-new-gray-900 body-14-regular whitespace-pre-line'>
              서로의 일정표를 공유하며{'\n'}완벽한 여행을 준비해 보세요!
            </p>
          </div>
        </div>
        <div className='px-20pxr'>
          {posts.map((post) => (
            <Link
              key={post.id}
              href={`/board/sharing-trips/${post.id}`}
              className='group py-16pxr flex gap-12pxr border-b border-new-gray-200'
            >
              {post.images?.[0] && (
                <div className='w-60pxr h-60pxr shrink-0 bg-new-gray-100 rounded-8pxr overflow-hidden'>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={post.images[0]} alt='썸네일 이미지' className='w-full h-full object-cover' />
                </div>
              )}
              <div className='flex-1 flex flex-col gap-8pxr'>
                <div className='flex items-center gap-12pxr justify-between'>
                  <div className='flex flex-col gap-4pxr'>
                    <div className='flex items-center gap-4pxr'>
                      <h4 className='text-new-gray-900 sub-title-14-bold pointer hover:group-hover:text-new-point-500 line-clamp-1'>
                        {post.title}
                      </h4>
                      {dayjs().diff(post.createdAt, 'day') <= 1 && (
                        <span className='w-16pxr h-16pxr shrink-0 bg-new-red-500 rounded-full flex items-center justify-center'>
                          <NewIcon67 />
                        </span>
                      )}
                    </div>

                    <article className='body-14-regular text-new-gray-800 line-clamp-1'>{post.content}</article>
                  </div>

                  {post.comments && post.comments.length > 0 && (
                    <div className='px-10pxr py-8pxr rounded-8pxr bg-new-gray-100 shrink-0 flex items-center gap-4pxr'>
                      <CommentBalloon1616 />
                      <span className='text-new-gray-800 body-14-regular'>{getCommentCount(post)}</span>
                    </div>
                  )}
                </div>
                <div className='flex flex-wrap items-center gap-8pxr body-12-regular text-new-gray-700'>
                  <span>{getPostType(post)}</span>
                  <div className='w-1pxr h-12pxr bg-new-gray-300'></div>
                  <span>{getDisplayCreatedAt(post.createdAt)}</span>
                  <div className='w-1pxr h-12pxr bg-new-gray-300'></div>
                  <span>조회 {post.viewCount}</span>
                  <div className='w-1pxr h-12pxr bg-new-gray-300'></div>
                  <span>일정표 복사 {post.copyCount}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {hasMore && !isLoading && <div ref={observerTargetRef} className='col-span-2 h-10pxr'></div>}
    </div>
  );
}
